import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AdvDetailsSection03() {
  const { t } = useTranslation();
  const ecoBenefits = [
    { icon: 'ri-user-star-line', title: t('advantages.s3.benefit.0.title'), desc: t('advantages.s3.benefit.0.desc') },
    { icon: 'ri-time-line', title: t('advantages.s3.benefit.1.title'), desc: t('advantages.s3.benefit.1.desc') },
    { icon: 'ri-building-2-line', title: t('advantages.s3.benefit.2.title'), desc: t('advantages.s3.benefit.2.desc') },
    { icon: 'ri-trophy-line', title: t('advantages.s3.benefit.3.title'), desc: t('advantages.s3.benefit.3.desc') },
  ];
  const compareData = Array.from({ length: 8 }, (_, i) => ({ feature: t(`advantages.s3.compare.${i}`), us: true, general: false }));

  return (
    <section className="bg-white py-8 space-y-0">
      {/* ADV 05: Customer-Oriented */}
      <div className="max-w-[1400px] mx-auto px-6 py-16">
        <div className="text-center mb-14">
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 bg-primary/10 border border-primary/20 rounded-full">
            <span className="text-primary text-xs font-mono font-bold">05</span>
            <span className="text-primary text-xs font-medium uppercase tracking-wider">{t('advantages.s3.tag')}</span>
          </div>
          <h2 className="text-4xl xl:text-5xl font-heading font-bold text-gray-900 mb-4 leading-tight">
            {t('advantages.s3.title1')}
            <span className="text-primary"> {t('advantages.s3.title2')}</span>
          </h2>
          <p className="text-gray-500 text-base max-w-2xl mx-auto">
            {t('advantages.s3.desc1')}
            {t('advantages.s3.desc2')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 mb-20">
          {ecoBenefits.map((b, i) => (
            <div
              key={i}
              className="group bg-white border border-primary/15 rounded-2xl p-7 hover:-translate-y-1 hover:border-primary/35 transition-all duration-300 text-center"
            >
              <div className="w-14 h-14 flex items-center justify-center bg-primary/10 border border-primary/20 rounded-xl mx-auto mb-5 group-hover:bg-primary/20 transition-colors">
                <i className={`${b.icon} text-primary text-2xl`} />
              </div>
              <h4 className="text-gray-900 font-bold text-sm mb-3">{b.title}</h4>
              <p className="text-gray-500 text-xs leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>

        {/* Comparison Table */}
        <div className="max-w-3xl mx-auto">
          <h3 className="text-2xl font-heading font-bold text-gray-900 text-center mb-8">
            {t('advantages.s3.compareTitle')}
          </h3>
          <div className="bg-white border border-primary/15 rounded-2xl overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-3 border-b border-primary/10 bg-pink-50/60">
              <div className="px-6 py-4 text-gray-500 text-sm font-medium">{t('advantages.s3.compareCol.0')}</div>
              <div className="px-6 py-4 text-center">
                <span className="text-primary text-sm font-bold">{t('advantages.s3.compareCol.1')}</span>
              </div>
              <div className="px-6 py-4 text-center">
                <span className="text-gray-400 text-sm">{t('advantages.s3.compareCol.2')}</span>
              </div>
            </div>
            {compareData.map((row, i) => (
              <div
                key={i}
                className={`grid grid-cols-3 border-b border-primary/8 last:border-0 ${i % 2 === 0 ? 'bg-pink-50/20' : 'bg-white'}`}
              >
                <div className="px-6 py-4 text-gray-700 text-sm">{row.feature}</div>
                <div className="px-6 py-4 flex justify-center">
                  <div className="w-6 h-6 flex items-center justify-center bg-primary/15 rounded-full">
                    <i className="ri-check-line text-primary text-xs" />
                  </div>
                </div>
                <div className="px-6 py-4 flex justify-center">
                  <div className="w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full">
                    <i className="ri-close-line text-gray-400 text-xs" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="max-w-[1400px] mx-auto px-6 pb-16">
        <div
          className="relative rounded-2xl overflow-hidden border border-primary/20 text-center py-16 px-6 bg-gradient-to-br from-pink-50 via-white to-rose-50"
        >
          <div className="absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage: 'radial-gradient(circle, rgba(255,45,120,0.8) 1px, transparent 1px)',
              backgroundSize: '40px 40px',
            }}
          />
          <div className="relative">
            <div className="text-5xl font-heading font-black text-gray-900 mb-4">
              {t('advantages.s3.cta.title')}
            </div>
            <p className="text-gray-500 text-base mb-10 max-w-xl mx-auto">
              {t('advantages.s3.cta.desc')}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary hover:bg-primary/90 text-white font-bold text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-message-3-line" />
                {t('nav.cta')}
              </Link>
              <Link
                to="/products"
                className="inline-flex items-center gap-2 px-8 py-4 border border-primary/40 text-primary hover:bg-primary/10 font-semibold text-sm rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap"
              >
                {t('advantages.s3.cta.btn2')}
                <i className="ri-arrow-right-line" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
